import React from 'react';
import { cn } from '../utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'bordered' | 'ghost' | 'default';
  inputSize?: 'xs' | 'sm' | 'md' | 'lg';
  state?: 'default' | 'success' | 'warning' | 'error';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'bordered',
      inputSize = 'md',
      state = 'default',
      fullWidth = true,
      leftIcon,
      rightIcon,
      leftAddon,
      rightAddon,
      className,
      ...props
    },
    ref
  ) => {
    const inputClasses = cn(
      'input',
      variant === 'bordered' && 'input-bordered',
      variant === 'ghost' && 'input-ghost',
      inputSize === 'xs' && 'input-xs',
      inputSize === 'sm' && 'input-sm',
      inputSize === 'lg' && 'input-lg',
      state === 'success' && 'input-success',
      state === 'warning' && 'input-warning',
      state === 'error' && 'input-error',
      fullWidth && 'w-full',
      (leftIcon || leftAddon) && 'pl-10',
      (rightIcon || rightAddon) && 'pr-10',
      className
    );

    if (leftIcon || rightIcon || leftAddon || rightAddon) {
      return (
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 z-10">
              {leftIcon}
            </span>
          )}
          {leftAddon && (
            <span className="absolute left-0 top-0 bottom-0 flex items-center px-3 bg-base-200 border-r border-base-300 rounded-l-lg">
              {leftAddon}
            </span>
          )}

          <input ref={ref} className={inputClasses} {...props} />

          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 z-10">
              {rightIcon}
            </span>
          )}
          {rightAddon && (
            <span className="absolute right-0 top-0 bottom-0 flex items-center px-3 bg-base-200 border-l border-base-300 rounded-r-lg">
              {rightAddon}
            </span>
          )}
        </div>
      );
    }

    return <input ref={ref} className={inputClasses} {...props} />;
  }
);

Input.displayName = 'Input';
